import cn from "classnames"

import { Alert, Card, Checkbox, Divider, Flex, Text } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { KAI_GUIDANCE_TAB_KEY } from "@/const/tab.constants"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import { LinkButton } from "@/molecules/LinkButton"
import { Form } from "@/organisms/Form"

import styles from "./KaiTab.module.scss"

import type { FormKaiTabProps } from "./KaiTab.types"

export const FormKaiTab = (props: FormKaiTabProps) => {
  const {
    initialValues,
    createTenantConfigurations,
    disabled,
    showAIProviderAlert,
    aiProviderConfigured,
    t
  } = props
  const { form, message } = Form.useForm()
  const { t: tGeneral } = useTranslation()
  const codeScan = "codeScan"
  const PRScan = "PRScan"
  const generateFix = "generateFix"
  const enableKai = "enableKai"
  const externalResources = "externalResources"
  const isOn: Record<string, boolean> = {
    [enableKai]: Form.useWatch(enableKai, form),
    [codeScan]: Form.useWatch(codeScan, form)
  }
  const getInitialValue = () => {
    const {
      kaiSastLlm,
      kaiChatbot,
      kaiAccessExternalWebSource,
      kaiSastPrs,
      kaiGenerateFix
    } = initialValues || {}

    return {
      [codeScan]: !!kaiSastLlm,
      [PRScan]: !!kaiSastPrs,
      [generateFix]: !!kaiGenerateFix,
      [enableKai]: !!kaiChatbot,
      [externalResources]: !!kaiAccessExternalWebSource
    }
  }

  const gap = 20

  return (
    <Form
      initialValues={getInitialValue()}
      onFinish={async (values) => {
        const action = "updated"
        const text = tGeneral("general.config")

        try {
          const payload = {
            kai_sast_llm: values[codeScan],
            kai_sast_prs: values[codeScan] ? values[PRScan] : false,
            kai_generate_fix: values[codeScan] ? values[generateFix] : false,
            kai_chatbot: values[enableKai],
            kai_access_external_web_source: values[enableKai]
              ? values[externalResources]
              : false
          }
          await createTenantConfigurations(payload)

          message.onSuccess(text, { action })
        } catch (error: unknown) {
          message.onFailed(text, { action, error })
        }
      }}
    >
      <Flex vertical gap={12}>
        {[enableKai, codeScan].map((key) => {
          return (
            <Card key={key} className={styles.card}>
              <Flex vertical gap={gap}>
                <Flex vertical>
                  <Heading level={5} title={t(key)} />
                </Flex>
                {key === enableKai && showAIProviderAlert && (
                  <Alert
                    showIcon
                    type="info"
                    title={t(
                      aiProviderConfigured
                        ? "aiProvider.fixRequired"
                        : "aiProvider.required"
                    )}
                  />
                )}
                <Form.Item noStyle name={key} valuePropName="checked">
                  <Checkbox
                    disabled={disabled && !isOn[key]}
                    onChange={(value) => {
                      if (key !== enableKai) return

                      form.setFieldsValue({
                        [externalResources]: value.target.checked
                      })
                    }}
                  >
                    {t(`${key}Description`)}
                  </Checkbox>
                </Form.Item>
                {key === enableKai && (
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const isEnableKaiEnabled = getFieldValue(enableKai)
                      return (
                        <Flex vertical gap={gap} className={styles.leftPadding}>
                          <Divider className={styles.divider} />
                          <Flex vertical gap={4}>
                            <Form.Item
                              noStyle
                              name={externalResources}
                              valuePropName="checked"
                            >
                              <Checkbox
                                disabled={
                                  !isEnableKaiEnabled ||
                                  (disabled &&
                                    !getFieldValue(externalResources))
                                }
                              >
                                {t(`${externalResources}Description`)}
                              </Checkbox>
                            </Form.Item>
                            <Text
                              type="secondary"
                              className={cn(
                                "sm-text-ks",
                                styles.leftPadding,
                                !isEnableKaiEnabled && styles.disabled
                              )}
                            >
                              {t(`${externalResources}Subtitle`, "")}
                            </Text>
                          </Flex>
                        </Flex>
                      )
                    }}
                  </Form.Item>
                )}
                {key === codeScan && (
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const isCodeScanEnabled = getFieldValue(codeScan)

                      return (
                        <Flex vertical gap={gap}>
                          <Flex
                            vertical
                            gap={gap}
                            className={styles.leftPadding}
                          >
                            <Divider className={styles.divider} />
                            <Flex vertical gap={4}>
                              <Form.Item
                                noStyle
                                name={PRScan}
                                valuePropName="checked"
                              >
                                <Checkbox
                                  disabled={
                                    !isCodeScanEnabled ||
                                    (disabled && !getFieldValue(PRScan))
                                  }
                                >
                                  {t(`${PRScan}Description`)}
                                </Checkbox>
                              </Form.Item>
                              <Text
                                type="secondary"
                                className={cn(
                                  "sm-text-ks",
                                  styles.leftPadding,
                                  !isCodeScanEnabled && styles.disabled
                                )}
                              >
                                {t(`${PRScan}Subtitle`, "")}
                              </Text>
                            </Flex>
                            <Divider className={styles.divider} />
                            <Flex vertical gap={4}>
                              <Form.Item
                                noStyle
                                name={generateFix}
                                valuePropName="checked"
                              >
                                <Checkbox
                                  disabled={
                                    !isCodeScanEnabled ||
                                    (disabled && !getFieldValue(generateFix))
                                  }
                                >
                                  {t(`${generateFix}Description`)}
                                </Checkbox>
                              </Form.Item>
                              <Text
                                type="secondary"
                                className={cn(
                                  "sm-text-ks",
                                  styles.leftPadding,
                                  !isCodeScanEnabled && styles.disabled
                                )}
                              >
                                {t(`${generateFix}Subtitle`, "")}
                              </Text>
                            </Flex>
                            <LinkButton
                              className={styles.guidanceLink}
                              to={`/${NAMES_ROUTES.SETTINGS.ROOT}/${KAI_GUIDANCE_TAB_KEY}`}
                              type="link"
                              disabled={!isCodeScanEnabled}
                            >
                              {t("addContext")}
                            </LinkButton>
                          </Flex>
                        </Flex>
                      )
                    }}
                  </Form.Item>
                )}
              </Flex>
            </Card>
          )
        })}
      </Flex>
    </Form>
  )
}
